import { BattleOutcome } from "../types/battle"
import { BossPointView } from "../types/boss"
import { normalizePointHistory } from "./pointHistory"

type RawBossPoint = Record<string, unknown>

function pickNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value
  if (typeof value === "string" && value.trim()) {
    const normalized = value.trim().replace(",", ".")
    const n = Number(normalized)
    return Number.isFinite(n) ? n : null
  }
  return null
}

function pickBool(value: unknown, fallback = true): boolean {
  if (typeof value === "boolean") return value
  if (value === 0 || value === "0") return false
  if (value === 1 || value === "1") return true
  return fallback
}

function normalizeBattleOutcome(value: unknown): BattleOutcome | null {
  const raw = String(value ?? "").trim().toUpperCase()
  if (raw === "WIN" || raw === "LOSE" || raw === "DRAW") return raw
  return null
}

function isRawBossPointShape(raw: RawBossPoint): boolean {
  const lat = pickNumber(raw.latitude ?? raw.lat)
  const lng = pickNumber(raw.longitude ?? raw.lng ?? raw.lon)
  return lat != null && lng != null
}

function unwrapRawPoint(raw: RawBossPoint): RawBossPoint {
  const nested = raw.point ?? raw.bossPoint ?? raw.boss_point
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    return { ...(nested as RawBossPoint), ...raw }
  }
  return raw
}

export function normalizeBossPoint(raw: RawBossPoint): BossPointView | null {
  const item = unwrapRawPoint(raw)
  const id =
    item.id ??
    item.uuid ??
    item.bossPointId ??
    item.boss_point_id

  const lat = pickNumber(item.latitude ?? item.lat)
  const lng = pickNumber(item.longitude ?? item.lng ?? item.lon)
  const radiusMeters =
    pickNumber(
      item.radiusMeters ??
        item.radius_meters ??
        item.radiusM ??
        item.radius_m ??
        item.radius
    ) ?? 50

  if (id == null || lat == null || lng == null) {
    return null
  }

  const name = String(item.name ?? item.title ?? "").trim()
  const lastOutcome = normalizeBattleOutcome(
    item.lastOutcome ?? item.last_outcome
  )

  return {
    id: String(id),
    name: name || `Босс ${id}`,
    latitude: lat,
    longitude: lng,
    radiusMeters,
    minVariance: pickNumber(item.minVariance ?? item.min_variance) ?? 0.85,
    maxVariance: pickNumber(item.maxVariance ?? item.max_variance) ?? 1.15,
    isAvailable: pickBool(item.isAvailable ?? item.is_available, true),
    cooldownUntil:
      (item.cooldownUntil ?? item.cooldown_until ?? null) as string | null,
    lastOutcome,
    history: (() => {
      const history = normalizePointHistory({ history: item.history ?? item.pointHistory })
      return history.events.length > 0 ? history : undefined
    })(),
  }
}

function collectArrays(...candidates: unknown[]): RawBossPoint[] {
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate as RawBossPoint[]
    }
  }
  return []
}

export function extractBossPointsPayload(data: unknown): RawBossPoint[] {
  if (typeof data === "string" && data.trim()) {
    try {
      return extractBossPointsPayload(JSON.parse(data))
    } catch {
      return []
    }
  }

  if (Array.isArray(data)) return data as RawBossPoint[]

  if (data && typeof data === "object") {
    const obj = data as Record<string, unknown>
    const nestedData =
      obj.data && typeof obj.data === "object" && !Array.isArray(obj.data)
        ? (obj.data as Record<string, unknown>)
        : null

    const fromArrays = collectArrays(
      obj.points,
      obj.items,
      obj.bossPoints,
      obj.boss_points,
      obj.result,
      obj.data,
      nestedData?.points,
      nestedData?.items,
      nestedData?.bossPoints,
      nestedData?.boss_points
    )
    if (fromArrays.length > 0) return fromArrays

    if (isRawBossPointShape(obj)) return [obj]
    if (nestedData && isRawBossPointShape(nestedData)) return [nestedData]
  }

  return []
}

export function mapBossPoints(data: unknown): BossPointView[] {
  return extractBossPointsPayload(data)
    .map(normalizeBossPoint)
    .filter((point): point is BossPointView => point != null)
}
